import React, { useState } from 'react';
import { generateMindfulPause } from '../services/geminiService';
import { MindfulPauseResponse } from '../types';
import { PauseCircle, RefreshCw, Wind, Feather } from 'lucide-react';
import { useUser } from '../context/UserContext';

const SlowDownPause: React.FC = () => {
  const [stressor, setStressor] = useState('');
  const [guide, setGuide] = useState<MindfulPauseResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp } = useUser();

  const handlePause = async () => {
    if (!stressor.trim()) return;
    setLoading(true);
    setGuide(null);
    try {
      const data = await generateMindfulPause(stressor);
      setGuide(data);
      addXp(40, 'Hit the Pause Button');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-purple-50 text-purple-600 rounded-full mb-4">
          <PauseCircle size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">The Pause Button</h1>
        <p className="text-nunchi-600">
          When the world feels like it's spinning too fast, the best thing to do is stop. <br/>
          Press pause and let your mind catch up with your body.
        </p>
      </header>

      {!guide && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                What is making you feel rushed or overwhelmed?
            </label>
            <textarea
              value={stressor}
              onChange={(e) => setStressor(e.target.value)}
              placeholder="e.g., Too many emails, I'm late for everything, Anxiety about the future..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-purple-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handlePause}
              disabled={!stressor.trim()}
              className="w-full bg-purple-700 text-white py-4 rounded-xl font-bold hover:bg-purple-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <PauseCircle size={18} /> Slow Down Now
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-purple-100 border-t-purple-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-purple-700">Taking a deep breath...</p>
        </div>
      )}

      {guide && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-white border border-nunchi-200 rounded-2xl p-8 shadow-lg relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 opacity-50 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
                 
                 <div className="mb-8">
                     <h3 className="text-purple-600 uppercase tracking-widest text-xs font-bold mb-3 flex items-center gap-2">
                         <Feather size={14} /> Gentle Perspective
                     </h3>
                     <p className="text-lg leading-relaxed font-serif text-nunchi-800">
                         "{guide.perspective}"
                     </p>
                 </div>

                 <div className="mb-8 bg-purple-50 p-6 rounded-xl border border-purple-100">
                     <h3 className="text-purple-800 uppercase tracking-widest text-xs font-bold mb-2 flex items-center gap-2">
                         <Wind size={14} /> Immediate Action
                     </h3>
                     <p className="text-purple-900 font-medium">{guide.action}</p>
                 </div>

                 <div className="text-center pt-4 border-t border-nunchi-100">
                     <p className="text-xl font-serif text-nunchi-600 italic">
                         "{guide.wisdom}"
                     </p>
                     <p className="text-xs text-nunchi-400 mt-2 uppercase tracking-widest">— Haemin Sunim Style</p>
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setGuide(null); setStressor(''); }}
                  className="text-purple-600 hover:text-purple-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Pause Again
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default SlowDownPause;